import { useState, useCallback } from "react";
import { useAppSelector } from "../../store/hooks";
import { useGetQuery, useRemoveMutation } from "../../store/slices/apiSlice";
import { Log } from "../../store/types";
import { getErrorMessage } from "./use-getError";

interface LogsApiProps {
  wellUid?: string | null;
  wellboreUid?: string | null;
  uid?: string | null;
  fetchAll: boolean;
  fetchLog?: boolean;
  fetchCurves?: boolean;
  fetchData?: boolean;
}

const buildLogsUrl = (
  wellsEP: string,
  wellUid: string,
  wellboresEP: string,
  wellboreUid: string,
  logsEP: string,
  uid?: string,
  suffix?: string
): string => {
  let url = `${wellsEP}/${wellUid}${wellboresEP}/${wellboreUid}${logsEP}`;
  if (uid) {
    url += `/${uid}`;
    if (suffix) url += `/${suffix}`;
  }
  return url;
};

export const useLogsAPI = ({
  wellUid,
  wellboreUid,
  uid,
  fetchAll = false,
  fetchLog = false,
  fetchCurves = false,
  fetchData = false,
}: LogsApiProps) => {
  const logsEP = useAppSelector((state) => state.endpoints.logs);
  const wellsEP = useAppSelector((state) => state.endpoints.wells);
  const wellboresEP = useAppSelector((state) => state.endpoints.wellbores);

  // lazy query
  const [targetWellUid, setTargetWellUid] = useState<string | null>(null);
  const [targetWellboreUid, setTargetWellboreUid] = useState<string | null>(
    null
  );

  const lazyEndpoint =
    targetWellUid && targetWellboreUid
      ? `${wellsEP}/${targetWellUid}${wellboresEP}/${targetWellboreUid}${logsEP}`
      : "";

  const { data, isLoading, refetch } = useGetQuery(lazyEndpoint, {
    skip: !targetWellUid || !targetWellboreUid,
  });

  const fetchLogs = useCallback((wellUid: string, wellboreUid: string) => {
    setTargetWellUid(wellUid);
    setTargetWellboreUid(wellboreUid);
  }, []);

  const clearLazy = useCallback(() => {
    setTargetWellUid(null);
    setTargetWellboreUid(null);
  }, []);

  // delete
  const [removeLog, { isLoading: isDeleting }] = useRemoveMutation();

  const deleteLog = useCallback(
    async (
      wellUid: string,
      wellboreUid: string,
      uid: string
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        const result = await removeLog({
          endpoint: buildLogsUrl(
            wellsEP,
            wellUid,
            wellboresEP,
            wellboreUid,
            logsEP
          ),
          id: uid,
        });
        if ("error" in result && result.error) {
          return { success: false, error: getErrorMessage(result.error) };
        }
        return { success: true };
      } catch (error) {
        return { success: false, error: String(error) };
      }
    },
    [removeLog, wellsEP, wellboresEP, logsEP]
  );

  // data query
  const hasBase = Boolean(
    wellsEP && wellUid && wellboresEP && wellboreUid && logsEP
  );

  const {
    data: allLogs,
    refetch: refetchAllLogs,
    isLoading: isFetchLoading,
  } = useGetQuery(
    buildLogsUrl(wellsEP!, wellUid!, wellboresEP!, wellboreUid!, logsEP!),
    {
      refetchOnMountOrArgChange: true,
      skip: !hasBase || !fetchAll,
    }
  );

  const {
    data: log,
    refetch: refetchLog,
    isLoading: isLogLoading,
  } = useGetQuery(
    hasBase && uid
      ? buildLogsUrl(
          wellsEP!,
          wellUid!,
          wellboresEP!,
          wellboreUid!,
          logsEP!,
          uid
        )
      : "",
    {
      refetchOnMountOrArgChange: true,
      skip: !hasBase || !uid || !fetchLog,
    }
  );

  const {
    data: logCurves,
    refetch: refetchLogCurves,
    isLoading: isCurvesLoading,
  } = useGetQuery(
    hasBase && uid
      ? buildLogsUrl(
          wellsEP!,
          wellUid!,
          wellboresEP!,
          wellboreUid!,
          logsEP!,
          uid,
          "curves"
        )
      : "",
    {
      refetchOnMountOrArgChange: true,
      skip: !hasBase || !uid || !fetchCurves,
    }
  );

  const {
    data: logData,
    refetch: refetchLogData,
    isLoading: isDataLoading,
  } = useGetQuery(
    hasBase && uid
      ? buildLogsUrl(
          wellsEP!,
          wellUid!,
          wellboresEP!,
          wellboreUid!,
          logsEP!,
          uid,
          "data"
        )
      : "",
    {
      refetchOnMountOrArgChange: true,
      skip: !hasBase || !uid || !fetchData,
    }
  );

  return {
    logsEP,
    fetchLogs,
    logs: data as Log[] | undefined,
    isLazyLoading: isLoading,
    refetchLazy: refetch,
    clearLazy,
    deleteLog,
    isDeleting,
    allLogs: allLogs as Log[] | undefined,
    refetchAllLogs,
    isFetchLoading,
    log,
    refetchLog,
    isLogLoading,
    logCurves,
    refetchLogCurves,
    isCurvesLoading,
    logData,
    refetchLogData,
    isDataLoading,
  };
};
